import { Button, Col, Collapse, Drawer, List, Row, Typography } from 'antd';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  DropboxOutlined,
  CodeSandboxOutlined,
  ClockCircleOutlined,
  BulbOutlined,
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleFilled,
  CloseCircleOutlined,
  SendOutlined,
  StopFilled
} from '@ant-design/icons';
import { getStatusDescription, getStatusName, log } from './LogStreamTable';
import { config } from 'aws-sdk';
import SentLogDetails from './SentLogDetails';
import FailureLogDetails from './FailureDetails';
import {
  getChannelReadableName,
  getChannelShortName
} from '../../utils/readable';
import {
  NotificationDetailsStatus,
  NotificationStatus
} from '../../interfaces/api/Logs';
import { green, orange, red, yellow } from '@ant-design/colors';
import { readWebsiteVariable } from '../../utils/readWebsiteVariable';
import moment from 'moment';
import 'moment-timezone';
import styled from 'styled-components';
import Loading from '../loading';
import { getChannelIcon } from '../notifications/NotificationsContainer';
import { Channels } from '../common/channelsEnum';
import DeliveredLogDetails from './DeliveredDetails';
import JsonDisplay from './JsonDisplay';
import IgnoredLogDetails from './IgnoredLogDetails';
import { SenderPOSTRequest } from '../../interfaces/backend/SenderPOSTRequest';
import Paragraph from 'antd/lib/typography/Paragraph';

const CustomTable = styled.table`
  border-collapse: separate;
  border-spacing: 0 16px;
`;

const { Text } = Typography;

config.region = readWebsiteVariable('REACT_APP_aws_project_region');

export type LogDetailProps = {
  trackingId: string;
  detailedLogs?: log[];
  timezone: string;
  setOpenedLog: Dispatch<SetStateAction<string | undefined>>;
};

export const getMessageExplanation = (m: string): JSX.Element => {
  if (m === 'Duplicate notification detected. Aborted.')
    return (
      <>
        <Text>Duplicate notification detected: </Text>
        <Text type="secondary">
          This request was ignored because it would have resulted in a duplicate
          notification. This behavior is set by the Deduplication setting on the
          notification.
        </Text>
      </>
    );

  if (
    m ===
    'Throttling. Based on your notification configuration, we have detected that this notification should be ignored. Aborted.'
  )
    return (
      <>
        <Text>Notification Request Throttled: </Text>
        <Text type="secondary">
          This request was ignored because it would have resulted in exceeding
          the maximum number of notifications allowed to be sent to this user as
          per the throttling settings on the notification.
        </Text>
      </>
    );
  else return <Text>{m}</Text>;
};

export default function LogDetails({
  trackingId,
  detailedLogs,
  timezone,
  setOpenedLog
}: LogDetailProps): JSX.Element {
  const [received, setReceived] = useState<log>();
  const [scheduled, setScheduled] = useState<log[]>();
  const [allChannelsIgnored, setAllChannelsIgnoredIgnored] = useState<log>();
  const [sending, setSending] = useState<log>();
  const [batching, setBatching] = useState<log>();
  const [batched, setBatched] = useState<log>();
  const [sents, setSents] = useState<log[]>();
  const [deliveries, setDeliveries] = useState<log[]>();
  const [failures, setFailures] = useState<log[]>();
  const [status, setStatus] = useState<string>();
  const [ignoredTemplatesLogs, setIgnoredTemplatesLogs] = useState<log[]>();
  const [ignoredDuplicationLogs, setIgnoredDuplicationLogs] = useState<log[]>();
  console.log(batched?.message.batchTrackingId, trackingId);
  useEffect(() => {
    setReceived(
      detailedLogs?.find(
        (l) => l.message.status === NotificationDetailsStatus.RECEIVED
      )
    );
    setScheduled(
      detailedLogs?.filter(
        (l) => l.message.status === NotificationDetailsStatus.SCHEDULED
      )
    );
    setAllChannelsIgnoredIgnored(
      detailedLogs?.find((l) => {
        return (
          l.message.status === NotificationDetailsStatus.IGNORED &&
          l.message.messages?.includes(
            'All delivery channels are disabled/discarded. Aborted.'
          )
        );
      })
    );
    setIgnoredTemplatesLogs(
      detailedLogs?.filter((l) => {
        return (
          l.message.status === NotificationDetailsStatus.IGNORED &&
          l.message.template
        );
      })
    );
    setIgnoredDuplicationLogs(
      detailedLogs?.filter((l) => {
        return (
          l.message.status === NotificationDetailsStatus.IGNORED &&
          l.message.messages?.includes(
            'Duplicate notification detected. Aborted.'
          )
        );
      })
    );
    setSending(
      detailedLogs?.find(
        (l) => l.message.status === NotificationDetailsStatus.SENDING
      )
    );
    setBatching(
      detailedLogs?.find(
        (l) => l.message.status === NotificationDetailsStatus.BATCHING
      )
    );
    setBatched(
      detailedLogs?.find(
        (l) => l.message.status === NotificationDetailsStatus.BATCHED
      )
    );

    setSents(
      detailedLogs?.filter(
        (l) => l.message.status === NotificationDetailsStatus.SENT
      )
    );
    setDeliveries(
      detailedLogs?.filter(
        (l) => l.message.status === NotificationDetailsStatus.DELIVERED
      )
    );
    setFailures(
      detailedLogs?.filter(
        (l) =>
          l.message.status === NotificationDetailsStatus.FAILURE ||
          l.message.status === NotificationDetailsStatus.COMPLAINT
      )
    );
  }, [detailedLogs]);
  // figure out the status without relying on summary logs, only through the detailed logs:
  useEffect(() => {
    // any channel failure => failure
    if (failures && failures.length > 0) {
      setStatus(NotificationStatus.FAILURE);
      return;
    }

    // ignored => success
    if (allChannelsIgnored) {
      setStatus(NotificationStatus.SUCCESS);
      return;
    }

    // all expected channels delivered => success
    if (
      sending &&
      sending.message.channels?.length &&
      sending.message.channels?.length > 0 &&
      deliveries &&
      deliveries.length > 0
    ) {
      const everyChannelDelivered = sending?.message.channels?.every((c) => {
        return deliveries.find((l) => l.message.channels?.[0] === c);
      });
      if (everyChannelDelivered) {
        setStatus(NotificationStatus.SUCCESS);
        return;
      }
    }

    // default:
    setStatus(NotificationStatus.RECEIVED);
  }, [failures, deliveries, allChannelsIgnored, received, sending]);

  return (
    <Drawer
      destroyOnClose
      open={true}
      onClose={() => setOpenedLog(undefined)}
      title="Log Details"
      size="large"
    >
      {!detailedLogs && <Loading />}
      {detailedLogs && (
        <Row gutter={[20, 20]}>
          <Col>
            <CustomTable>
              <tbody>
                <tr>
                  <td style={{ width: '100px', verticalAlign: 'top' }}>
                    <Text type="secondary">Status</Text>
                  </td>
                  <td>
                    <Text>{getStatusName(status)}</Text>
                    <br />
                    <Text type="secondary">{getStatusDescription(status)}</Text>
                  </td>
                </tr>
                <tr>
                  <td style={{ width: '100px', verticalAlign: 'top' }}>
                    <Text type="secondary">Tracking ID</Text>
                  </td>
                  <td>
                    <Text>{trackingId}</Text>
                  </td>
                </tr>
                <tr>
                  <td style={{ width: '100px', verticalAlign: 'top' }}>
                    <Text type="secondary">Timestamp</Text>
                  </td>
                  <td>
                    <Text>
                      {' '}
                      {moment(received?.timestamp)
                        .tz(timezone)
                        .format('YYYY-MM-DD HH:mm:ss')}
                    </Text>
                  </td>
                </tr>
                <tr>
                  <td style={{ width: '100px', verticalAlign: 'top' }}>
                    <Text type="secondary">NotificationId</Text>
                  </td>
                  <td>
                    <Text>{received?.message.notificationId}</Text>
                  </td>
                </tr>
                <tr>
                  <td style={{ width: '100px', verticalAlign: 'top' }}>
                    <Text type="secondary">User</Text>
                  </td>
                  <td>
                    <Text>
                      {
                        // eslint-disable-next-line
                        // @ts-ignore
                        received?.message.body?.user?.id
                      }
                    </Text>
                    {
                      // eslint-disable-next-line
                      // @ts-ignore
                      received?.message.body?.user?.email && (
                        <>
                          <br />
                          <Text type="secondary">
                            {
                              // eslint-disable-next-line
                              // @ts-ignore
                              received?.message.body.user?.email
                            }
                          </Text>
                        </>
                      )
                    }
                    {
                      // eslint-disable-next-line
                      // @ts-ignore
                      received?.message.body?.user?.number && (
                        <>
                          <br />
                          <Text type="secondary">
                            {
                              // eslint-disable-next-line
                              // @ts-ignore
                              received?.message.body.user?.number
                            }
                          </Text>
                        </>
                      )
                    }
                  </td>
                </tr>
              </tbody>
            </CustomTable>
          </Col>

          <Col span={24}>
            <Collapse>
              {/* RECEIVED */}
              {received && (
                <Collapse.Panel
                  key="received"
                  header={
                    <Row justify="space-between">
                      <Col>
                        <CheckOutlined
                          style={{ color: green.primary, marginRight: '8px' }}
                        />
                        <Text>Received by NotificationAPI</Text>
                      </Col>
                      <Col>
                        <Text type="secondary">
                          {moment(received.timestamp)
                            .tz(timezone)
                            .format('YYYY-MM-DD HH:mm:ss')}
                        </Text>
                      </Col>
                    </Row>
                  }
                >
                  <Text type="secondary">Original API Request:</Text>
                  <JsonDisplay
                    json={received.message.body as Record<string, unknown>}
                  />
                </Collapse.Panel>
              )}

              {/* SCHEDULED */}
              {scheduled?.map((log) => {
                const isUpdatedLog =
                  log.message.updated &&
                  log.message?.messages?.includes('Schedule updated');

                const scheduleDatetime = isUpdatedLog
                  ? log.message?.updated?.schedule
                  : log.message.schedule;

                return (
                  <Collapse.Panel
                    key={`scheduled_${log.timestamp}`}
                    header={
                      <Row justify="space-between">
                        <Col>
                          <CheckOutlined
                            style={{ color: green.primary, marginRight: '8px' }}
                          />
                          <Text>
                            {isUpdatedLog ? 'Request updated' : 'Scheduled'}
                          </Text>
                        </Col>
                        <Col>
                          <Text type="secondary">
                            {moment(log.timestamp)
                              .tz(timezone)
                              .format('YYYY-MM-DD HH:mm:ss')}
                          </Text>
                        </Col>
                      </Row>
                    }
                  >
                    <Text type="secondary">Scheduled for:</Text>
                    <Row>
                      <Col>
                        {moment
                          .utc(scheduleDatetime)
                          .format('YYYY-MM-DD HH:mm:ss z')}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        {moment
                          .utc(scheduleDatetime)
                          .tz(timezone)
                          .format('YYYY-MM-DD HH:mm:ss z')}
                      </Col>
                    </Row>
                    {isUpdatedLog && (
                      <>
                        <Row>
                          <Text type="secondary">Updated request</Text>
                        </Row>
                        <JsonDisplay
                          json={log.message.updated as Record<string, unknown>}
                        />
                      </>
                    )}
                  </Collapse.Panel>
                );
              })}

              {/* IGNORED */}
              {allChannelsIgnored && (
                <Collapse.Panel
                  key="ignored"
                  header={
                    <Row justify="space-between">
                      <Col>
                        <StopFilled
                          style={{ color: orange.primary, marginRight: '8px' }}
                        />
                        <Text>Request Ignored By Design</Text>
                      </Col>
                      <Col>
                        <Text type="secondary">
                          {moment(allChannelsIgnored.timestamp)
                            .tz(timezone)
                            .format('YYYY-MM-DD HH:mm:ss')}
                        </Text>
                      </Col>
                    </Row>
                  }
                >
                  {allChannelsIgnored.message.messages &&
                    allChannelsIgnored.message.messages.length > 0 && (
                      <ul>
                        {allChannelsIgnored.message.messages.map((m, index) => (
                          <li key={index}>{getMessageExplanation(m)}</li>
                        ))}
                      </ul>
                    )}
                </Collapse.Panel>
              )}
              {/* BATCHING */}
              {batching && (
                <Collapse.Panel
                  key="batching"
                  header={
                    <Row justify="space-between">
                      <Col>
                        <DropboxOutlined
                          style={{ color: green.primary, marginRight: '8px' }}
                        />
                        <Text>Batching</Text>
                      </Col>
                      <Col>
                        <Text type="secondary">
                          {moment(batching.timestamp)
                            .tz(timezone)
                            .format('YYYY-MM-DD HH:mm:ss')}
                        </Text>
                      </Col>
                    </Row>
                  }
                >
                  {batching.message &&
                    batching.message.batchingChannels?.length &&
                    batching.message.batchingChannels?.length > 0 && (
                      <>
                        <Text type="secondary">Batching Channels:</Text>
                        <ul>
                          {batching.message.batchingChannels?.map(
                            (m, index) => (
                              <li key={index}>{m}</li>
                            )
                          )}
                        </ul>
                      </>
                    )}
                </Collapse.Panel>
              )}
              {/* BATCHED */}
              {batched && (
                <Collapse.Panel
                  key="batched"
                  header={
                    <Row justify="space-between">
                      <Col>
                        <CodeSandboxOutlined
                          style={{ color: green.primary, marginRight: '8px' }}
                        />
                        <Text>Batched</Text>
                      </Col>
                      <Col>
                        <Text type="secondary">
                          {moment(batched.timestamp)
                            .tz(timezone)
                            .format('YYYY-MM-DD HH:mm:ss')}
                        </Text>
                      </Col>
                    </Row>
                  }
                >
                  {batched.message && batched.message.batchSchedule && (
                    <>
                      <ClockCircleOutlined
                        style={{ color: green.primary, marginRight: '8px' }}
                      />
                      <Text>
                        Batch is scheduled for:{' '}
                        {moment(batched.message.batchSchedule)
                          .tz(timezone)
                          .format('YYYY-MM-DD HH:mm:ss')}
                      </Text>
                    </>
                  )}
                  {batched.message &&
                    batched.message.channels?.length &&
                    batched.message.channels?.length > 0 &&
                    batched.message.batchTrackingId &&
                    batched.message.batchTrackingId !== trackingId && (
                      <>
                        <Text type="secondary">Batching trackingId:</Text>
                        {batched.message.channels?.map((m, index) => (
                          <>
                            <div key={index}>
                              {getChannelIcon(m as Channels)} {m}:{' '}
                              <Button
                                onClick={() => {
                                  setOpenedLog(batched.message.batchTrackingId);
                                }}
                              >
                                {batched.message.batchTrackingId}
                              </Button>
                            </div>
                          </>
                        ))}
                      </>
                    )}
                </Collapse.Panel>
              )}
              {/* SENDING */}
              {sending && (
                <Collapse.Panel
                  key="sending"
                  header={
                    <Row justify="space-between">
                      <Col>
                        <BulbOutlined
                          style={{ color: green.primary, marginRight: '8px' }}
                        />
                        <Text>Initial Processing Results</Text>
                      </Col>
                      <Col>
                        <Text type="secondary">
                          {moment(sending.timestamp)
                            .tz(timezone)
                            .format('YYYY-MM-DD HH:mm:ss')}
                        </Text>
                      </Col>
                    </Row>
                  }
                >
                  {sending.message.messages &&
                    sending.message.messages.length > 0 && (
                      <>
                        <Text type="secondary">Messages & Warnings:</Text>
                        <ul>
                          {sending.message.messages.map((m, index) => (
                            <li key={index}>{m}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  {sending.message.channels &&
                    sending.message.channels.length > 0 && (
                      <>
                        <Text type="secondary">
                          Channels picked for delivery:
                        </Text>
                        <div style={{ marginLeft: 24, marginTop: 8 }}>
                          {sending.message.channels.map((c) => (
                            <Row gutter={8} key={c}>
                              <Col>
                                {getChannelIcon(c as unknown as Channels)}
                              </Col>
                              <Col>{getChannelReadableName(c)}</Col>
                            </Row>
                          ))}
                        </div>
                      </>
                    )}
                </Collapse.Panel>
              )}

              {/* GENERAL FAILURES */}
              {failures &&
                failures
                  .filter((f) => !f.message.channels)
                  .map((log) => {
                    return (
                      <Collapse.Panel
                        key={log.timestamp}
                        header={
                          <Row justify="space-between">
                            <Col>
                              <CloseCircleOutlined
                                style={{
                                  color: red.primary,
                                  marginRight: '8px'
                                }}
                              />
                              <Text>Failed</Text>
                            </Col>
                            <Col>
                              <Text type="secondary">
                                {moment(log.timestamp)
                                  .tz(timezone)
                                  .format('YYYY-MM-DD HH:mm:ss')}
                              </Text>
                            </Col>
                          </Row>
                        }
                      >
                        <FailureLogDetails log={log} />
                      </Collapse.Panel>
                    );
                  })}
            </Collapse>
          </Col>
          {ignoredTemplatesLogs?.map((l) => (
            <>
              <Col span={24}>
                <>
                  <div style={{ marginBottom: 4, marginLeft: 4 }}>
                    {getChannelIcon(l.message.channels as unknown as Channels)}{' '}
                    <Text type="secondary">
                      {getChannelShortName(
                        l.message.channels as unknown as Channels
                      )}
                    </Text>
                  </div>
                </>
                <Collapse>
                  <Collapse.Panel
                    key={l.timestamp}
                    header={
                      <Row justify="space-between">
                        <Col>
                          <CloseCircleFilled
                            style={{
                              color: yellow.primary,
                              marginRight: '8px'
                            }}
                          />
                          <Text>Ignored</Text>
                          <Text type="secondary"> (see preview)</Text>
                        </Col>
                        <Col>
                          <Text type="secondary">
                            {moment(l.timestamp)
                              .tz(timezone)
                              .format('YYYY-MM-DD HH:mm:ss')}
                          </Text>
                        </Col>
                      </Row>
                    }
                  >
                    <IgnoredLogDetails
                      ignoredLog={l}
                      timezone={timezone}
                      receivedMessageBody={
                        received?.message.body as SenderPOSTRequest
                      }
                    />
                  </Collapse.Panel>
                </Collapse>
              </Col>
            </>
          ))}
          {ignoredDuplicationLogs?.map((l) => (
            <>
              <Col span={24}>
                <Collapse>
                  <Collapse.Panel
                    key={l.timestamp}
                    header={
                      <Row justify="space-between">
                        <Col>
                          <CloseCircleFilled
                            style={{
                              color: yellow.primary,
                              marginRight: '8px'
                            }}
                          />
                          <Text>Ignored</Text>
                        </Col>
                        <Col>
                          <Text type="secondary">
                            {moment(l.timestamp)
                              .tz(timezone)
                              .format('YYYY-MM-DD HH:mm:ss')}
                          </Text>
                        </Col>
                      </Row>
                    }
                  >
                    <List>
                      {l.message.messages?.map((message) => (
                        <Paragraph>{message}</Paragraph>
                      ))}
                    </List>
                  </Collapse.Panel>
                </Collapse>
              </Col>
            </>
          ))}
          {sending?.message.channels?.map((c) => (
            <Col span={24} key={c}>
              <>
                <div style={{ marginBottom: 4, marginLeft: 4 }}>
                  {getChannelIcon(c as unknown as Channels)}{' '}
                  <Text type="secondary">{getChannelShortName(c)}</Text>
                </div>

                <Collapse>
                  {/* SENT */}
                  {sents &&
                    sents
                      .filter((l) => l.message.channels?.[0] === c)
                      .map((log) => {
                        return (
                          <Collapse.Panel
                            key={log.timestamp}
                            header={
                              <Row justify="space-between">
                                <Col>
                                  <SendOutlined
                                    style={{
                                      color: green.primary,
                                      marginRight: '8px'
                                    }}
                                  />
                                  <Text>Sent</Text>
                                  <Text type="secondary"> (see preview)</Text>
                                </Col>
                                <Col>
                                  <Text type="secondary">
                                    {moment(log.timestamp)
                                      .tz(timezone)
                                      .format('YYYY-MM-DD HH:mm:ss')}
                                  </Text>
                                </Col>
                              </Row>
                            }
                          >
                            <SentLogDetails sentLog={log} timezone={timezone} />
                          </Collapse.Panel>
                        );
                      })}

                  {/* DELIVERIES */}
                  {deliveries &&
                    deliveries
                      .filter((l) => l.message.channels?.[0] === c)
                      .map((log) => {
                        return (
                          <Collapse.Panel
                            key={log.timestamp}
                            header={
                              <Row justify="space-between">
                                <Col>
                                  <CheckCircleFilled
                                    style={{
                                      color: green.primary,
                                      marginRight: '8px'
                                    }}
                                  />
                                  <Text>Delivered</Text>
                                </Col>
                                <Col>
                                  <Text type="secondary">
                                    {moment(log.timestamp)
                                      .tz(timezone)
                                      .format('YYYY-MM-DD HH:mm:ss')}
                                  </Text>
                                </Col>
                              </Row>
                            }
                          >
                            <DeliveredLogDetails log={log} />
                          </Collapse.Panel>
                        );
                      })}

                  {/* FAILURES */}
                  {failures &&
                    failures
                      .filter((f) => f.message.channels?.[0] === c)
                      .map((log) => {
                        return (
                          <Collapse.Panel
                            key={log.timestamp}
                            header={
                              <Row justify="space-between">
                                <Col>
                                  <CloseCircleOutlined
                                    style={{
                                      color: red.primary,
                                      marginRight: '8px'
                                    }}
                                  />
                                  <Text>Failed</Text>
                                </Col>
                                <Col>
                                  <Text type="secondary">
                                    {moment(log.timestamp)
                                      .tz(timezone)
                                      .format('YYYY-MM-DD HH:mm:ss')}
                                  </Text>
                                </Col>
                              </Row>
                            }
                          >
                            <FailureLogDetails log={log} />
                          </Collapse.Panel>
                        );
                      })}
                </Collapse>
              </>
            </Col>
          ))}
          {batched &&
            batched?.message.batched &&
            Object.keys(batched?.message.batched).map((c) => (
              <Col span={24} key={c}>
                <>
                  {/* SENT */}
                  {sents &&
                    sents
                      .filter((l) => l.message.channels?.[0] === c)
                      .map((log) => {
                        return (
                          <>
                            <div style={{ marginBottom: 4, marginLeft: 4 }}>
                              {getChannelIcon(c as unknown as Channels)}{' '}
                              <Text type="secondary">
                                Batched {getChannelShortName(c)}
                              </Text>
                            </div>
                            <Collapse>
                              <Collapse.Panel
                                key={log.timestamp}
                                header={
                                  <Row justify="space-between">
                                    <Col>
                                      <SendOutlined
                                        style={{
                                          color: green.primary,
                                          marginRight: '8px'
                                        }}
                                      />
                                      <Text>Sent</Text>
                                      <Text type="secondary">
                                        {' '}
                                        (see preview)
                                      </Text>
                                    </Col>
                                    <Col>
                                      <Text type="secondary">
                                        {moment(log.timestamp)
                                          .tz(timezone)
                                          .format('YYYY-MM-DD HH:mm:ss')}
                                      </Text>
                                    </Col>
                                  </Row>
                                }
                              >
                                <SentLogDetails
                                  sentLog={log}
                                  timezone={timezone}
                                />
                              </Collapse.Panel>
                              {/* DELIVERIES */}
                              {deliveries &&
                                deliveries
                                  .filter((l) => l.message.channels?.[0] === c)
                                  .map((log) => {
                                    return (
                                      <Collapse.Panel
                                        key={log.timestamp}
                                        header={
                                          <Row justify="space-between">
                                            <Col>
                                              <CheckCircleFilled
                                                style={{
                                                  color: green.primary,
                                                  marginRight: '8px'
                                                }}
                                              />
                                              <Text>Delivered</Text>
                                            </Col>
                                            <Col>
                                              <Text type="secondary">
                                                {moment(log.timestamp)
                                                  .tz(timezone)
                                                  .format(
                                                    'YYYY-MM-DD HH:mm:ss'
                                                  )}
                                              </Text>
                                            </Col>
                                          </Row>
                                        }
                                      >
                                        <DeliveredLogDetails log={log} />
                                      </Collapse.Panel>
                                    );
                                  })}
                            </Collapse>
                          </>
                        );
                      })}

                  {/* FAILURES */}
                  {failures &&
                    failures
                      .filter((f) => f.message.channels?.[0] === c)
                      .map((log) => {
                        return (
                          <Collapse.Panel
                            key={log.timestamp}
                            header={
                              <Row justify="space-between">
                                <Col>
                                  <CloseCircleOutlined
                                    style={{
                                      color: red.primary,
                                      marginRight: '8px'
                                    }}
                                  />
                                  <Text>Failed</Text>
                                </Col>
                                <Col>
                                  <Text type="secondary">
                                    {moment(log.timestamp)
                                      .tz(timezone)
                                      .format('YYYY-MM-DD HH:mm:ss')}
                                  </Text>
                                </Col>
                              </Row>
                            }
                          >
                            <FailureLogDetails log={log} />
                          </Collapse.Panel>
                        );
                      })}
                </>
              </Col>
            ))}
          {status === NotificationStatus.RECEIVED &&
            !scheduled &&
            moment(received?.timestamp).isBefore(
              moment().subtract(30, 'minutes')
            ) && (
              <Col span={24}>
                <Collapse>
                  <Collapse.Panel
                    key="unknown"
                    header={
                      <Row justify="space-between">
                        <Col>
                          <CloseCircleOutlined
                            style={{
                              color: red.primary,
                              marginRight: '8px'
                            }}
                          />
                          <Text>Unknown Issue</Text>
                        </Col>
                      </Row>
                    }
                  >
                    <Text type="secondary">
                      The notification is marked with this issue because we
                      haven't receivied all the necessary logs to determine the
                      status of the notification after 30 minutes. Please
                      contact support so we can look into this for you.
                    </Text>
                  </Collapse.Panel>
                </Collapse>
              </Col>
            )}
        </Row>
      )}
    </Drawer>
  );
}
